/**
 * Base page wrapper, for containing page styles
 * and tabs to navigate to various sections
 * of a given API
 */

import React, {
    ReactNode,
    ReactChild,
    useEffect,
    useMemo,
    useCallback
} from "react"
import { DashboardBase } from "../../DashboardBase/DashboardBase"
import { AppInstanceStatus } from "../../../../../../Models/API/AppInstanceModel"
import { connect } from "react-redux"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { hasValue, getValue } from "../../../../../../Models/Client/FetchState"
import { AppStatusBanner } from "../../../Common/AppStatusBanner/AppStatusBanner"
import {
    startMonitorApp,
    stopMonitorApp
} from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { Menu } from "./Menu"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { APIJobQueryModel } from "../../../../../../Models/API/JobModel"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { useErrorTrackingInfo } from "../../../../../../Hooks/useErrorTrackingInfo"
import { useCanLabelApp } from "../../../../../../Hooks/useCanLabelApp"
import { UserPlanExpiryBanner } from "../../../Common/UserPlanExpiryBanner/UserPlanExpiryBanner"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { ContactForm } from "../../../../../Common/Modal/ContactForm"
import { ContactUsMessageType } from "../../../../../../Models/External/FeedbackModel"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import {
    selectUserId,
    selectFreemiumPlanExpiryDays
} from "../../../../../../Helpers/Functions/Selectors/auth"
import { AppAIStatusBanner } from "../../../Common/AppStatusBanner/AppAIStatusBanner"

export interface AppDetailBaseProps {
    orgUsername: string
    id: string
    title: ReactNode
    breadcrumbs: string[]
    fixedContentWidth: boolean
    filterAndSort?: FilterAndSortExpression<APIJobQueryModel>
    renderSidebar?: () => ReactChild | undefined
    renderModalView?: () => ReactChild | undefined
    /**
     * @deprecated use `children` instead.
     */
    render?: () => ReactNode
    children?: ReactNode
    hideBanner?: boolean
    forceUserExpiryContact?: boolean
    resetUserExpiryContact?: () => void
}

interface DispatchProps {
    doStartMonitorApp: () => void
    doStopMonitorApp: () => void
}

type Props = AppDetailBaseProps & DispatchProps

const AppDetailBaseIsolated = ({
    orgUsername,
    id,
    title,
    render,
    renderSidebar,
    renderModalView,
    doStartMonitorApp,
    doStopMonitorApp,
    fixedContentWidth,
    breadcrumbs,
    children,
    filterAndSort,
    hideBanner,
    forceUserExpiryContact,
    resetUserExpiryContact
}: Props) => {
    useErrorTrackingInfo("superai.appID", id)
    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const templateFetch = useAppTemplate(id)
    const ownerID = useAppState(selectUserId)

    const freemiumPlanExpiryDays = useAppState(selectFreemiumPlanExpiryDays)

    useErrorTrackingInfo("superai.templateName", getValue(templateFetch)?.name)

    const instanceFetch = useAppInstance(id)

    const { canLabel, couldLabel } = useCanLabelApp(id)

    const handleUserExpiryContactClose = useCallback(() => {
        hideUserExpiryContact()
        resetUserExpiryContact?.()
    }, [hideUserExpiryContact, resetUserExpiryContact])

    const menu = useMemo(
        () =>
            hasValue(instanceFetch) ? (
                <Menu
                    orgUsername={orgUsername}
                    appID={id}
                    filterAndSort={filterAndSort}
                    canLabel={canLabel}
                    couldLabel={couldLabel}
                />
            ) : null,
        [instanceFetch, orgUsername, id, filterAndSort, canLabel, couldLabel]
    )
    const banner = useMemo(
        () =>
            freemiumPlanExpiryDays === undefined ||
            freemiumPlanExpiryDays > 4 ? (
                hideBanner ? undefined : (
                    <>
                        <AppStatusBanner
                            orgUsername={orgUsername}
                            appID={id}
                            canLabel={canLabel}
                        />
                        <AppAIStatusBanner appID={id} />
                    </>
                )
            ) : (
                <UserPlanExpiryBanner
                    expiryDays={freemiumPlanExpiryDays}
                    onContact={showUserExpiryContact}
                />
            ),
        [
            freemiumPlanExpiryDays,
            hideBanner,
            orgUsername,
            id,
            canLabel,
            showUserExpiryContact
        ]
    )

    const instanceStatus = getValue(instanceFetch)?.status
    const me = useAppState(state => state.api.authentication.user)

    // monitor app owner status if the app instance is enabled
    useEffect(() => {
        if (instanceStatus === AppInstanceStatus.ENABLED) {
            doStartMonitorApp()
            return doStopMonitorApp
        }
    }, [instanceStatus]) // eslint-disable-line react-hooks/exhaustive-deps

    const modal = useMemo(() => {
        if (userExpiryContact || forceUserExpiryContact) {
            return hasValue(instanceFetch) ? (
                <ContactForm
                    prefilledMessage="I'd like to talk about upgrading my trial"
                    extraData={{
                        ownerID: ownerID ?? "unknown",
                        appID: instanceFetch.value.uuid,
                        type: ContactUsMessageType.TrialExpired
                    }}
                    onClose={handleUserExpiryContactClose}
                />
            ) : undefined
        }
        return renderModalView?.()
    }, [
        forceUserExpiryContact,
        handleUserExpiryContactClose,
        instanceFetch,
        ownerID,
        renderModalView,
        userExpiryContact
    ])

    return (
        <DashboardBase
            orgUsername={orgUsername}
            title={title}
            breadcrumbs={
                hasValue(instanceFetch) && me !== null
                    ? [
                          orgUsername === me.username
                              ? "Personal account"
                              : orgUsername,
                          "Projects",
                          instanceFetch.value.name
                      ].concat(breadcrumbs)
                    : []
            }
            fixedContentWidth={fixedContentWidth}
            secondaryMenuContent={menu}
            id={id}
            sidebar={renderSidebar?.()}
            modalView={modal}
            banner={banner}
        >
            {children ? children : render ? render() : null}
        </DashboardBase>
    )
}

const mapDispatchToProps = (
    dispatch: MainThunkDispatch,
    { id }: AppDetailBaseProps
): DispatchProps => {
    return {
        doStartMonitorApp: () => dispatch(startMonitorApp(id)),
        doStopMonitorApp: () => dispatch(stopMonitorApp())
    }
}

export const AppDetailBase = connect(
    null,
    mapDispatchToProps
)(AppDetailBaseIsolated)
